// src/frontend/src/App.tsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import './App.css';

// Eagerly load the home page for instant first paint
import HomePage from './pages/HomePage';
import ErrorBoundary from './components/ErrorBoundary';
import Analytics from './analytics/Analytics';

// Lazy load other pages for code splitting
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const AZGuidePage = lazy(() => import('./pages/AZGuidePage'));
const LocationsPage = lazy(() => import('./pages/LocationsPage'));
const SortingDetailPage = lazy(() => import('./pages/SortingDetailPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ScanPage = lazy(() => import('./pages/ScanPage'));
const FeesPage = lazy(() => import('./pages/FeesPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
// Admin is a separate, password-gated tool; keeping it lazy means its Leaflet
// editor and API client never ship in the bundle a normal visitor downloads.
const AdminPage = lazy(() => import('./pages/AdminPage'));

// Minimal loading fallback for fast perceived performance
const PageLoader: React.FC = () => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--color-surface-deep, #0B1215)'
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(255,255,255,0.1)',
            borderTopColor: 'var(--color-neon-lime, #CCFF00)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
        }} />
    </div>
);

function App() {
    return (
        <Router>
            {/* Inside the router so it can see navigation, outside the
                boundary so a render failure is still reported. */}
            <Analytics />
            {/* Outside Suspense so it also catches a lazy chunk that fails to
                load, not only a throw from a mounted page. */}
            <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/calendar" element={<CalendarPage />} />
                        <Route path="/az-guide" element={<AZGuidePage />} />
                        <Route path="/locations" element={<LocationsPage />} />
                        <Route path="/waste-type/:id" element={<SortingDetailPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/scan" element={<ScanPage />} />
                        <Route path="/fees" element={<FeesPage />} />
                        <Route path="/help" element={<HelpCenterPage />} />
                        <Route path="/privacy" element={<PrivacyPolicyPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/admin" element={<AdminPage />} />
                    </Routes>
                </Suspense>
            </ErrorBoundary>
        </Router>
    );
}

export default App;
